import React, { useEffect, useMemo, useRef, useState } from "react";
import * as styles from "./new-sidebar.module.scss";

import { useGSAP } from "@gsap/react";
import CloseIcon from "@mui/icons-material/Close";
import { IconButton } from "@mui/material";
import { Link, navigate } from "gatsby";
import { gsap } from "gsap";
import { useTranslation } from "react-i18next";
import logoBrown from "../../../images/svg/logo-vert-brown.svg";
import logo from "../../../images/svg/logo-vert.svg";
import logoHorizontal from "../../../images/svg/Logo.svg";
import { useNav } from "../../../shared/context";
import { useCartContext } from "../../../shared/context/cart.context";
import { useDeviceContext } from "../../../shared/context/device.context";
import { I18N_LOCAL_STORAGE_KEY } from "../../../shared/locales";
import { classNames } from "../../../shared/utils/utils";

const CartIcon = () => {
  return (
    <svg
      width="56"
      height="56"
      viewBox="0 0 56 56"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <rect x="0.5" y="0.5" width="55" height="55" rx="7.5" stroke="white" />
      <path
        d="M36.8879 24.5L33.3322 14M19.1096 24.5L22.6652 14M24.4431 31.5C24.4431 32.4283 24.8177 33.3185 25.4845 33.9749C26.1513 34.6313 27.0557 35 27.9987 35C28.9417 35 29.8461 34.6313 30.513 33.9749C31.1798 33.3185 31.5544 32.4283 31.5544 31.5C31.5544 30.5717 31.1798 29.6815 30.513 29.0251C29.8461 28.3687 28.9417 28 27.9987 28C27.0557 28 26.1513 28.3687 25.4845 29.0251C24.8177 29.6815 24.4431 30.5717 24.4431 31.5ZM15.5561 21H40.4439C40.9566 20.9999 41.4632 21.109 41.929 21.3197C42.3949 21.5304 42.8089 21.8378 43.1428 22.2208C43.4766 22.6038 43.7224 23.0533 43.8632 23.5385C44.004 24.0238 44.0366 24.5332 43.9587 25.032L41.7275 37.548C41.5338 38.7878 40.8956 39.9183 39.9283 40.735C38.9611 41.5516 37.7288 42.0004 36.4545 42H19.5438C18.2698 41.9999 17.0379 41.551 16.071 40.7344C15.1041 39.9178 14.4661 38.7875 14.2725 37.548L12.0413 25.032C11.9634 24.5332 11.996 24.0238 12.1368 23.5385C12.2776 23.0533 12.5234 22.6038 12.8572 22.2208C13.1911 21.8378 13.6051 21.5304 14.071 21.3197C14.5368 21.109 15.0434 20.9999 15.5561 21Z"
        stroke="white"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
};

const BurgerIcon = () => (
  <svg viewBox="0 0 60 40" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M7 7L50.9019 4"
      stroke="currentColor"
      strokeWidth="8"
      strokeLinecap="round"
    ></path>
    <path
      d="M11.1372 20H56"
      stroke="currentColor"
      strokeWidth="8"
      strokeLinecap="round"
    ></path>
    <path
      d="M4 36L49 36"
      stroke="currentColor"
      strokeWidth="8"
      strokeLinecap="round"
    ></path>
  </svg>
);

type ToggleButtonProps = {
  isOpen: boolean;
  onToggle: () => void;
  className?: string;
  ariaLabel: string;
  ariaControls?: string;
};

const ToggleButton = ({
  isOpen,
  onToggle,
  className,
  ariaLabel,
  ariaControls,
}: ToggleButtonProps) => (
  <IconButton
    className={className}
    onClick={onToggle}
    aria-label={ariaLabel}
    aria-expanded={isOpen}
    aria-controls={ariaControls}
    disableRipple
  >
    {isOpen ? <CloseIcon fontSize="large" /> : <BurgerIcon />}
  </IconButton>
);

const navItems = [
  { name: "nav.home", path: "/" },
  { name: "nav.menu", path: "/menu" },
  { name: "nav.restaurant", path: "/restaurant" },
  // { name: "nav.new_year_sets", path: "/menu#new-year-sets" },
  { name: "nav.about", path: "/about-us" },
  { name: "nav.delivery", path: "/delivery" },
];

// temp
const langItems = [
  { code: "pl", img: "/images/svg/flag1.svg", altKey: "nav.polish" },
  { code: "en", img: "/images/svg/flag2.svg", altKey: "nav.english" },
  // { code: "ua", img: "/images/svg/flag3.svg", altKey: "nav.ukrainian" },
];

export const NewSidebar = () => {
  const [menuVisible, setMenuVisible] = useState<boolean>(false);
  const [bump, setBump] = useState(false);

  const { hidden, isDarkNavTheme, navAbsolute, forceShown } = useNav();
  const { productsCount, cartRef, setCartRef } = useCartContext();
  const { isMobile } = useDeviceContext();

  const desktopCartRef = useRef<HTMLDivElement | null>(null);
  const mobileCartRef = useRef<HTMLDivElement | null>(null);

  const { t, i18n } = useTranslation();

  const toggleMenu = () => setMenuVisible((visible) => !visible);

  const activeLang = useMemo(() => {
    return i18n.language;
  }, [i18n.language]);

  const availableLanguages = useMemo(() => {
    return i18n.languages;
  }, [i18n]);

  const availableLangItems = useMemo(() => {
    return langItems;
  }, [availableLanguages]);

  const handleLangChange = (langCode: string) => {
    i18n.changeLanguage(langCode);

    if (typeof window !== "undefined") {
      localStorage.setItem(I18N_LOCAL_STORAGE_KEY, langCode);
    }

    setMenuVisible(false);
  };

  const openMenuLabel = t("nav.open_menu", { defaultValue: "Open menu" });
  const closeMenuLabel = t("nav.close_menu", { defaultValue: "Close menu" });

  const toggleAriaLabel = menuVisible ? closeMenuLabel : openMenuLabel;

  const container = useRef<HTMLDivElement>(null);
  const navMenu = useRef<HTMLDivElement>(null);
  const border = useRef<HTMLDivElement>(null);
  const items = useRef<HTMLLIElement[]>([]);
  const langs = useRef<HTMLElement[]>([]);

  const menuId = "new-sidebar-menu";

  useGSAP(
    () => {
      if (menuVisible) {
        const tl = gsap.timeline();
        tl.to(navMenu.current, {
          duration: 1,
          clipPath: "polygon(0 0, 100% 0, 100% 100%, 0% 100%)",
          ease: "power3.inOut",
        })
          .from(
            items.current,
            {
              duration: 0.6,
              y: 70,
              stagger: 0.05,
              ease: "power4.inOut",
            },
            "-=0.85"
          )
          .to(
            border.current,
            {
              duration: 1,
              scaleX: 1,
              ease: "power4.inOut",
            },
            "-=0.8"
          )
          .to(
            langs.current,
            {
              duration: 0.5,
              y: 0,
              stagger: {
                amount: 0.3,
                ease: "power4.inOut",
              },
            },
            "-=0.6"
          );
      } else {
        const tl = gsap.timeline();
        tl.to(navMenu.current, {
          duration: 0.6,
          opacity: 0,
          ease: "power2.out",
          onComplete: () => {
            gsap.set(navMenu.current, {
              clipPath: "polygon(0 0, 0 0, 0 100%, 0% 100%)",
              opacity: 1,
            });
            gsap.set(border.current, { scaleX: 0 });
            gsap.set(langs.current, { y: "10px" });
          },
        });
      }
    },
    { dependencies: [menuVisible], scope: container } // działa w kontekście reactowym
  );

  const navClassName = [
    styles.nav,
    !menuVisible && hidden && !forceShown && styles.nav__hidden,
    isDarkNavTheme && styles.navDarkTheme,
    navAbsolute && styles.navAbsolute,
  ]
    .filter(Boolean)
    .join(" ");

  const navMenuClassName = [styles.nav__menu, menuVisible && styles.active]
    .filter(Boolean)
    .join(" ");

  useEffect(() => {
    if (isMobile) {
      setCartRef(mobileCartRef.current);
    } else {
      setCartRef(desktopCartRef.current);
    }
  }, [isMobile]);

  useEffect(() => {
    if (productsCount === 0) return;
    setBump(true);
    const timer = setTimeout(() => setBump(false), 300);
    return () => clearTimeout(timer);
  }, [productsCount]);

  return (
    <>
      <div className={styles.cartButtonWrappContainer}>
        <div className={styles.cartButtonWrapp}>
          <div
            ref={desktopCartRef}
            className={classNames(
              styles,
              styles.cartButton,
              styles.cartButtonDesktop
            )}
            onClick={() => navigate("/order")}
          >
            <CartIcon />
            {productsCount > 0 && (
              <div
                className={classNames(
                  styles,
                  styles.cartCount,
                  bump ? styles.bump : ""
                )}
              >
                {productsCount}
              </div>
            )}
          </div>
        </div>
      </div>

      <nav ref={container} className={navClassName}>
        <div className={styles.mobileNavbar}>
          <div className={styles.mobileNavbar__logo}>
            <img
              src={logoHorizontal}
              alt="Daigo Sushi"
              loading="lazy"
              onClick={() => {
                navigate("/");
                setMenuVisible(false);
              }}
            />
          </div>

          <div className={styles.mobileRightSection}>
            <div
              ref={mobileCartRef}
              className={classNames(
                styles,
                styles.cartButton,
                styles.cartButtonMobile
              )}
              onClick={() => navigate("/order")}
            >
              <CartIcon />
              {productsCount > 0 && (
                <div
                  className={classNames(
                    styles,
                    styles.cartCount,
                    bump ? styles.bump : ""
                  )}
                >
                  {productsCount}
                </div>
              )}
            </div>

            <ToggleButton
              isOpen={menuVisible}
              onToggle={toggleMenu}
              ariaLabel={toggleAriaLabel}
              ariaControls={menuId}
              className={`${styles.toggleButton} ${styles.mobileNavbar__toggle}`}
            />
          </div>
        </div>

        <div className={styles.burger}>
          <ToggleButton
            isOpen={menuVisible}
            onToggle={toggleMenu}
            ariaLabel={toggleAriaLabel}
            ariaControls={menuId}
            className={`${styles.toggleButton} ${styles.burger__button}`}
          />
        </div>

        <div
          className={styles.logo}
          onClick={() => {
            navigate("/");
            setMenuVisible(false);
          }}
        >
          <img src={isDarkNavTheme ? logoBrown : logo} />
        </div>

        <ul className={styles.social}>
          <li className={styles.social__list}>
            <a
              href="https://www.instagram.com/daigo.sushi?igsh=dDh0ZWo1MXdvMHZ4"
              className={styles.social__link}
              target="_blank"
            >
              <svg
                viewBox="0 0 27 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M5 8C5 6.93913 5.44777 5.92172 6.2448 5.17157C7.04183 4.42143 8.12283 4 9.25 4H17.75C18.8772 4 19.9582 4.42143 20.7552 5.17157C21.5522 5.92172 22 6.93913 22 8V16C22 17.0609 21.5522 18.0783 20.7552 18.8284C19.9582 19.5786 18.8772 20 17.75 20H9.25C8.12283 20 7.04183 19.5786 6.2448 18.8284C5.44777 18.0783 5 17.0609 5 16V8Z"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                ></path>
                <path
                  d="M10.3125 12C10.3125 12.7956 10.6483 13.5587 11.2461 14.1213C11.8439 14.6839 12.6546 15 13.5 15C14.3454 15 15.1561 14.6839 15.7539 14.1213C16.3517 13.5587 16.6875 12.7956 16.6875 12C16.6875 11.2044 16.3517 10.4413 15.7539 9.87868C15.1561 9.31607 14.3454 9 13.5 9C12.6546 9 11.8439 9.31607 11.2461 9.87868C10.6483 10.4413 10.3125 11.2044 10.3125 12Z"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                ></path>
                <path
                  d="M18.2812 7.5V7.51"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                ></path>
              </svg>
            </a>
          </li>

          <li className={styles.social__list}>
            <a
              href="https://www.tiktok.com/@daigo.sushi?_t=ZN-8x9CwkVeuaE&amp;_r=1"
              className={styles.social__link}
              target="_blank"
            >
              <svg
                viewBox="0 0 27 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M23.0625 7.917V11.951C21.1352 11.771 19.2975 11.0961 17.75 10V14.5C17.7497 15.7211 17.384 16.9175 16.6946 17.952C16.0053 18.9865 15.0203 19.8174 13.8524 20.3493C12.6845 20.8813 11.3811 21.0929 10.0913 20.9599C8.80162 20.8268 7.57779 20.3545 6.56007 19.5971C5.54236 18.8397 4.77192 17.8278 4.33703 16.6773C3.90213 15.5268 3.82037 14.2843 4.10111 13.0921C4.38185 11.8999 5.01374 10.8062 5.92438 9.93642C6.83502 9.0666 7.98757 8.4558 9.25 8.174V12.5C8.88778 12.7557 8.60102 13.0943 8.41694 13.4836C8.23286 13.8729 8.15757 14.3001 8.19823 14.7245C8.2389 15.1489 8.39415 15.5565 8.64926 15.9086C8.90437 16.2606 9.25087 16.5455 9.65584 16.7361C10.0608 16.9266 10.5108 17.0166 10.9631 16.9975C11.4155 16.9783 11.8551 16.8507 12.2402 16.6266C12.6254 16.4026 12.9433 16.0896 13.1638 15.7174C13.3843 15.3452 13.5 14.9261 13.5 14.5V3H17.8382C18.0594 4.2284 18.6811 5.36162 19.6172 6.24263C20.5533 7.12363 21.7573 7.70878 23.0625 7.917Z"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                ></path>
              </svg>
            </a>
          </li>
        </ul>

        <div
          ref={navMenu}
          className={navMenuClassName}
          id={menuId}
          aria-hidden={!menuVisible}
        >
          <div className={styles.nav__content}>
            <div className={styles.nav__close}>
              <IconButton
                className={styles.closeBtn}
                onClick={() => setMenuVisible(false)}
              >
                <CloseIcon fontSize="large" className={styles.closeIcon} />
              </IconButton>
            </div>
            <div className={styles.nav__list}>
              <ul className={styles.nav__ul}>
                {navItems.map((item, index) => (
                  <li
                    key={index}
                    className={styles.nav__item}
                    ref={(el) => (items.current[index] = el!)}
                  >
                    <Link
                      to={item.path}
                      className={styles.nav__link}
                      activeClassName={styles.active}
                      onClick={() => setMenuVisible(false)}
                    >
                      {t(item.name)}
                    </Link>
                  </li>
                ))}
              </ul>

              <div ref={border} className={styles.nav__border}></div>
            </div>
            <div className={styles.nav__img}>
              <img src="/images/page-home-small.webp" />
            </div>

            {/* <div className={styles.nav__contentSpace}></div> */}

            <div className={styles.nav__lang}>
              <div className={styles.nav__langWrapp}>
                <div className={styles.nav__langTxt}>
                  {t("nav.select_language")}
                </div>
                <ul className={styles.nav__langUl}>
                  {availableLangItems.map((item, index) => {
                    const langClassName = [
                      styles.nav__langLi,
                      activeLang === item.code && styles.active,
                    ]
                      .filter(Boolean)
                      .join(" ");

                    return (
                      <li
                        ref={(el) => (langs.current[index] = el!)}
                        key={item.code}
                        className={langClassName}
                      >
                        <button
                          onClick={() => handleLangChange(item.code)}
                          className={styles.nav__langBtn}
                        >
                          <img src={item.img} alt={t(item.altKey)} />
                        </button>
                      </li>
                    );
                  })}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </nav>
    </>
  );
};
